<script lang="ts">
  // Tela mostrada quando jogador venceu uma partida do torneio mas a próxima
  // fase ainda não foi spawnada (a match irmã ainda não terminou).
  // Atualiza sozinha via WS push (tournament:state) — quando o sibling
  // terminar e a próxima match for criada, o backend emite game:state e
  // o roteamento global leva pra GameView.
  import type { Tournament } from '../../stores/tournaments';
  import { userId } from '../../stores/auth';
  import { bracketModalOpen, bracketTournament } from '../../stores/tournaments';

  type Props = {
    tournament: Tournament;
    // Round atual em que o jogador venceu (sempre tem um match dele lá com winner).
    currentRoundIdx: number;
    // Index da match do jogador na round atual (acessar via t.bracket.rounds[currentRoundIdx].matches[playerMatchIdx]).
    playerMatchIdx: number;
  };
  let { tournament: t, currentRoundIdx, playerMatchIdx }: Props = $props();

  function pidName(pid: string): string {
    if (pid === $userId) return 'VOCÊ';
    return t.player_names?.[pid] || t.bot_names?.[pid] || (pid.startsWith('bot-') ? 'bot' : pid.slice(0, 8));
  }

  function teamName(team: string[] | undefined): string {
    if (!team || team.length === 0) return '?';
    return team.map(pidName).join(' / ');
  }

  function phaseLabel(roundIdx: number, matchesInRound: number): string {
    if (matchesInRound === 1) return 'Final';
    if (matchesInRound === 2) return 'Semifinal';
    if (matchesInRound === 4) return 'Quartas de Final';
    if (matchesInRound === 8) return 'Oitavas de Final';
    if (matchesInRound === 16) return '16-avos de Final';
    if (matchesInRound === 32) return '32-avos de Final';
    if (matchesInRound === 64) return '64-avos de Final';
    return `Fase ${roundIdx + 1}`;
  }

  // Sibling: a match irmã do jogador (par dele). Quando essa decidir,
  // próxima fase começa.
  let siblingIdx = $derived(playerMatchIdx ^ 1);
  let siblingMatch = $derived(t.bracket?.rounds?.[currentRoundIdx]?.matches?.[siblingIdx]);

  // Nome da próxima fase.
  let nextRoundIdx = $derived(currentRoundIdx + 1);
  let nextRound = $derived(t.bracket?.rounds?.[nextRoundIdx]);
  let nextPhaseLabel = $derived(
    nextRound ? phaseLabel(nextRoundIdx, nextRound.matches.length) : 'Próxima fase',
  );

  // Match em que o jogador venceu — pra mostrar seu próprio time confirmado.
  let myMatch = $derived(t.bracket?.rounds?.[currentRoundIdx]?.matches?.[playerMatchIdx]);
  let myTeam = $derived.by<string[]>(() => {
    if (!myMatch || !myMatch.winner) return [];
    return myMatch.winner === 'A' ? myMatch.team_a : myMatch.team_b;
  });

  // Adversário definido?
  let siblingDecided = $derived(!!siblingMatch?.winner);
  let nextOpponent = $derived.by<string[]>(() => {
    if (!siblingDecided || !siblingMatch) return [];
    return siblingMatch.winner === 'A' ? siblingMatch.team_a : siblingMatch.team_b;
  });

  function openBracket() {
    bracketTournament.set(t);
    bracketModalOpen.set(true);
  }
</script>

<div class="waiting">
  <div class="card">
    <div class="trophy-wrap">
      <svg class="trophy" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M6 4h12v4a6 6 0 0 1-12 0V4z" stroke="currentColor" stroke-width="1.5" fill="rgba(108,255,189,0.18)"/>
        <path d="M6 6H4a2 2 0 0 0 0 4h2M18 6h2a2 2 0 0 1 0 4h-2" stroke="currentColor" stroke-width="1.5"/>
        <path d="M10 14h4v3h2v2H8v-2h2v-3z" fill="currentColor" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
      </svg>
    </div>

    <h1>Você venceu!</h1>
    <p class="sub">Avançou pra <strong>{nextPhaseLabel}</strong></p>

    <div class="team-row">
      {#each myTeam as pid}
        <span class="pid me">{pidName(pid)}</span>
      {/each}
    </div>

    <div class="vs-divider">
      <span class="vs-text">aguardando adversário</span>
    </div>

    {#if siblingDecided}
      <p class="ready">Adversário definido!</p>
      <div class="team-row">
        {#each nextOpponent as pid}
          <span class="pid opp">{pidName(pid)}</span>
        {/each}
      </div>
      <p class="spawning">Partida sendo iniciada<span class="dots">…</span></p>
    {:else}
      <p class="pending">Outro confronto ainda em andamento:</p>
      <div class="candidates">
        <span class="pid maybe">{teamName(siblingMatch?.team_a)}</span>
        <span class="vs-text">×</span>
        <span class="pid maybe">{teamName(siblingMatch?.team_b)}</span>
      </div>
      <p class="hint">Vencedor desse confronto vai jogar contra você. <br>Tá rolando agora<span class="dots">…</span></p>
    {/if}

    <button class="bracket-btn" onclick={openBracket}>
      🏆 VER CHAVEAMENTO
    </button>

    <div class="pulse"></div>
  </div>
</div>

<style>
  .waiting {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80vh;
    padding: 20px;
  }
  .card {
    max-width: 480px;
    width: 100%;
    padding: 32px 28px;
    background: var(--bg-elev);
    border: 1px solid rgba(108, 255, 189, 0.35);
    border-radius: var(--radius-lg);
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.7), 0 0 30px rgba(108, 255, 189, 0.08);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    position: relative;
    overflow: hidden;
  }
  .trophy-wrap {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(108, 255, 189, 0.18), transparent 70%);
  }
  .trophy {
    width: 48px;
    height: 48px;
    color: #ffd24a;
    filter: drop-shadow(0 0 8px rgba(255, 210, 74, 0.6));
  }
  h1 {
    margin: 0;
    font-size: 24px;
    color: var(--ok);
    letter-spacing: 2px;
    text-transform: uppercase;
  }
  .sub {
    margin: 0;
    color: var(--text-dim);
    font-size: 14px;
    letter-spacing: 1px;
  }
  .sub strong {
    color: var(--warn);
    font-weight: 800;
  }
  .team-row {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: center;
    margin: 6px 0;
  }
  .pid {
    padding: 6px 14px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border);
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;
    color: var(--text);
  }
  .pid.me {
    border-color: var(--ok);
    background: rgba(108, 255, 189, 0.1);
    color: var(--ok);
  }
  .pid.opp {
    border-color: var(--accent);
    background: rgba(216, 80, 240, 0.1);
    color: var(--accent);
  }
  .pid.maybe {
    border-color: rgba(255, 210, 74, 0.4);
    background: rgba(255, 210, 74, 0.05);
    color: var(--warn);
  }
  .vs-divider {
    display: flex;
    align-items: center;
    width: 100%;
    margin: 4px 0;
  }
  .vs-divider::before,
  .vs-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
  .vs-text {
    padding: 0 12px;
    font-size: 10px;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 2px;
  }
  .candidates {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .pending, .hint, .ready, .spawning {
    margin: 0;
    color: var(--text-dim);
    font-size: 12px;
    letter-spacing: 0.5px;
  }
  .ready {
    color: var(--accent);
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }
  .spawning, .hint {
    font-size: 11px;
    color: var(--text-dim);
  }
  .bracket-btn {
    margin-top: 8px;
    padding: 12px 24px;
    background: linear-gradient(180deg, #ffd54f, #ffb300 55%, #ff8f00);
    color: #0D47A1;
    border: 2px solid #e65100;
    border-radius: 10px;
    font-weight: 900;
    font-size: 13px;
    letter-spacing: 1.5px;
    cursor: pointer;
    box-shadow: 0 3px 0 #e65100, inset 0 1px 0 rgba(255, 255, 255, 0.4);
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
    transition: transform 0.1s, box-shadow 0.1s;
  }
  .bracket-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 0 #e65100, inset 0 1px 0 rgba(255, 255, 255, 0.4); }
  .bracket-btn:active { transform: translateY(2px); box-shadow: 0 1px 0 #e65100; }

  .dots {
    animation: dots 1.5s infinite;
    display: inline-block;
    width: 1.2em;
    text-align: left;
  }
  @keyframes dots {
    0%, 20% { opacity: 0; }
    50% { opacity: 1; }
    100% { opacity: 0; }
  }
  .pulse {
    position: absolute;
    inset: -1px;
    border-radius: var(--radius-lg);
    pointer-events: none;
    box-shadow: 0 0 0 0 rgba(108, 255, 189, 0.5);
    animation: pulse 2s infinite;
  }
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(108, 255, 189, 0.4); }
    70% { box-shadow: 0 0 0 14px rgba(108, 255, 189, 0); }
    100% { box-shadow: 0 0 0 0 rgba(108, 255, 189, 0); }
  }
</style>
